import { useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import logo from "../../static/logo.webp";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";

export default function Sidebar({ open, onClose, drawerWidth = 280 }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [competitionOpen, setCompetitionOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const isCompetitionCategoriesSelected =
    location.pathname.startsWith("/admin/competition") &&
    location.search.includes("categories");

  const isCompetitionTeamsSelected =
    location.pathname.startsWith("/admin/competition") &&
    location.search.includes("teams");

  const isCalendarVenuesSelected =
    location.pathname.startsWith("/admin/matches") &&
    location.search.includes("venues");

  const isCalendarMatchesSelected =
    location.pathname.startsWith("/admin/matches") &&
    location.search.includes("matches");

  const isCompetitionSectionActive =
    isCompetitionCategoriesSelected || isCompetitionTeamsSelected;

  const isCalendarSectionActive =
    isCalendarVenuesSelected || isCalendarMatchesSelected;

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: drawerWidth,
          overflow: "hidden",
          backgroundColor: "#8C0F2C",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#8C0F2C",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(255,255,255,0.14)",
            backgroundColor: "#fff",
          }}
        >
          <Box component="img" src={logo} alt="logo" sx={{ height: 52 }} />

          <IconButton onClick={onClose} sx={{ color: "#8C0F2C" }}>
            <ChevronLeftRoundedIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: "#fff",
            }}
          >
            ADMINISTRACIÓN
          </Typography>
        </Box>

        <List sx={{ px: 1.25 }}>
          <ListItemButton
            onClick={() => setCompetitionOpen((prev) => !prev)}
            sx={{
              borderRadius: "14px",
              mb: 0.75,
              minHeight: 48,
              color: "#fff",
              backgroundColor: isCompetitionSectionActive
                ? "rgba(255,255,255,0.12)"
                : "transparent",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.08)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "#fff" }}>
              <EmojiEventsRoundedIcon />
            </ListItemIcon>

            <ListItemText
              primary="Competencia"
              primaryTypographyProps={{
                fontSize: "0.94rem",
                fontWeight: 700,
                color: "#fff",
              }}
            />

            {competitionOpen ? (
              <ExpandLessRoundedIcon sx={{ color: "#fff" }} />
            ) : (
              <ExpandMoreRoundedIcon sx={{ color: "#fff" }} />
            )}
          </ListItemButton>

          <Collapse in={competitionOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItemButton
                onClick={() =>
                  handleNavigate("/admin/competition?view=categories")
                }
                selected={isCompetitionCategoriesSelected}
                sx={{
                  pl: 3.2,
                  borderRadius: "12px",
                  mb: 0.6,
                  minHeight: 44,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(255,255,255,0.16)",
                  },
                }}
              >
                <ListItemText
                  primary="Categorías"
                  primaryTypographyProps={{
                    fontSize: "0.92rem",
                    fontWeight: 700,
                    color: "#fff",
                  }}
                />
              </ListItemButton>

              <ListItemButton
                onClick={() => handleNavigate("/admin/competition?view=teams")}
                selected={isCompetitionTeamsSelected}
                sx={{
                  pl: 3.2,
                  borderRadius: "12px",
                  mb: 0.6,
                  minHeight: 44,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(255,255,255,0.16)",
                  },
                }}
              >
                <ListItemText
                  primary="Equipos"
                  primaryTypographyProps={{
                    fontSize: "0.92rem",
                    fontWeight: 700,
                    color: "#fff",
                  }}
                />
              </ListItemButton>
            </List>
          </Collapse>

          <ListItemButton
            onClick={() => setCalendarOpen((prev) => !prev)}
            sx={{
              borderRadius: "14px",
              mb: 0.75,
              minHeight: 48,
              color: "#fff",
              backgroundColor: isCalendarSectionActive
                ? "rgba(255,255,255,0.12)"
                : "transparent",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.08)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "#fff" }}>
              <CalendarMonthRoundedIcon />
            </ListItemIcon>

            <ListItemText
              primary="Calendario"
              primaryTypographyProps={{
                fontSize: "0.94rem",
                fontWeight: 700,
                color: "#fff",
              }}
            />

            {calendarOpen ? (
              <ExpandLessRoundedIcon sx={{ color: "#fff" }} />
            ) : (
              <ExpandMoreRoundedIcon sx={{ color: "#fff" }} />
            )}
          </ListItemButton>

          <Collapse in={calendarOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItemButton
                onClick={() => handleNavigate("/admin/matches?view=venues")}
                selected={isCalendarVenuesSelected}
                sx={{
                  pl: 3.2,
                  borderRadius: "12px",
                  mb: 0.6,
                  minHeight: 44,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(255,255,255,0.16)",
                  },
                }}
              >
                <ListItemText
                  primary="Sedes"
                  primaryTypographyProps={{
                    fontSize: "0.92rem",
                    fontWeight: 700,
                    color: "#fff",
                  }}
                />
              </ListItemButton>

              <ListItemButton
                onClick={() => handleNavigate("/admin/matches?view=matches")}
                selected={isCalendarMatchesSelected}
                sx={{
                  pl: 3.2,
                  borderRadius: "12px",
                  mb: 0.6,
                  minHeight: 44,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(255,255,255,0.16)",
                  },
                }}
              >
                <ListItemText
                  primary="Partidos"
                  primaryTypographyProps={{
                    fontSize: "0.92rem",
                    fontWeight: 700,
                    color: "#fff",
                  }}
                />
              </ListItemButton>
            </List>
          </Collapse>

          <ListItemButton
            onClick={() => handleNavigate("/admin/gallery")}
            sx={{
              borderRadius: "14px",
              mt: 0.8,
              minHeight: 48,
              color: "#fff",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.08)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "#fff" }}>
              <PhotoLibraryRoundedIcon />
            </ListItemIcon>

            <ListItemText
              primary="Galería"
              primaryTypographyProps={{
                fontSize: "0.94rem",
                fontWeight: 700,
                color: "#fff",
              }}
            />
          </ListItemButton>
          
          <ListItemButton
            onClick={() => handleNavigate("/")}
            sx={{
              borderRadius: "14px",
              mt: 0.8,
              minHeight: 48,
              color: "#fff",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.08)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "#fff" }}>
              <LanguageRoundedIcon />
            </ListItemIcon>
            

            <ListItemText
              primary="Ir al sitio web"
              primaryTypographyProps={{
                fontSize: "0.94rem",
                fontWeight: 700,
                color: "#fff",
              }}
            />
          </ListItemButton>
        </List>
      </Box>
    </Drawer>
  );
}